import { useEffect, useRef, useState } from 'react';
import { formatScore } from './ScoreBurst';

/**
 * A score that rolls up to a new value, as Balatro's does: it counts up, each digit that changes
 * rolling in from below, and glows while it climbs. A value that goes down (a new round) is shown
 * at once.
 */
export function RollingNumber({
  value,
  ms = 750,
  reduceMotion = false,
  className = '',
}: {
  value: number;
  /** How long the climb takes. */
  ms?: number;
  reduceMotion?: boolean;
  className?: string;
}) {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  const [rolling, setRolling] = useState(false);
  useEffect(() => {
    const from = shownRef.current;
    const show = (n: number) => {
      shownRef.current = n;
      setShown(n);
    };
    if (reduceMotion || value <= from) {
      show(value);
      setRolling(false);
      return;
    }
    let frame = 0;
    const start = performance.now();
    setRolling(true);
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      // Fast at first, settling on the score.
      show(Math.round(from + (value - from) * (1 - (1 - k) ** 3)));
      if (k < 1) frame = requestAnimationFrame(step);
      else setRolling(false);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, ms, reduceMotion]);
  // Long Night scores grow past what fits: shown short (1.24M, 4.2e12) as they climb.
  const digits = formatScore(shown).split('');
  return (
    <span className={`rolling ${rolling ? 'is-rolling' : ''} ${className}`}>
      {digits.map((d, i) => (
        // Keyed by place and digit: a digit that changes rolls in.
        <span key={`${digits.length - i}-${d}`} className="rolling-digit">
          {d}
        </span>
      ))}
    </span>
  );
}
